import React from "react";
import { useNavigate } from "react-router-dom";
import { Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { company } from "../mock";

import { tr, L } from "../i18n";
const linkCols = L([
  {
    title: "Solutions",
    links: [
      { label: "Business", to: "/solutions/business" },
      { label: "Personal", to: "/solutions/personal" },
      { label: "Charities & Schools", to: "/solutions/charities" },
      { label: "Accountants", to: "/solutions/accountants" },
      { label: "Advisers", to: "/solutions/advisers" },
    ],
  },
  {
    title: "Platform",
    links: [
      { label: "How it works", to: "/#how-it-works" },
      { label: "Interest calculator", to: "/#calculator" },
      { label: "FSCS protection", to: "/fscs" },
      { label: "Currencies", to: "/currencies" },
      { label: "Security", to: "/security" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About us", to: "/about" },
      { label: "Insights", to: "/insights" },
      { label: "Careers", to: "/careers" },
      { label: "Contact", to: "/contact" },
      { label: "Legal & privacy", to: "/legal" },
    ],
  },
]);

export default function Footer() {
  const navigate = useNavigate();

  const go = (to) => {
    if (to.startsWith("/#")) {
      if (window.location.pathname === "/") {
        const el = document.getElementById(to.replace("/#", ""));
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate(to);
      }
    } else {
      navigate(to);
      window.scrollTo(0, 0);
    }
  };

  return (
    <footer className="bg-[#08192f] text-white pt-16 pb-8 relative overflow-hidden">
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-[#c2a15a] to-transparent" />

      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-5 gap-10 pb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <button
              onClick={() => go("/")}
              className="flex items-center gap-2 mb-5"
            >
              <span className="font-display text-[28px] text-white">
                {company.brandMark}
              </span>
              <span className="flex flex-col leading-none text-left">
                <span className="text-[10px] tracking-[0.35em] text-[#c2a15a] font-medium">
                  {company.brandSub}
                </span>
                <span className="text-[9px] tracking-[0.2em] text-white/50">
                  {tr("LAWYERS LLP")}
                </span>
              </span>
            </button>
            <p className="text-white/55 text-[14px] leading-relaxed max-w-sm">
              {tr("A cash savings platform giving clients access to competitive rates across 55+ banks and building societies — with diversification and FSCS protection on eligible deposits.")}
            </p>

            <div className="mt-6 space-y-2.5 text-[13px] text-white/60">
              <p className="flex items-start gap-2">
                <MapPin size={15} className="text-[#c2a15a] mt-0.5 shrink-0" />
                {company.address}
              </p>
              <a
                href={`mailto:${company.email}`}
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <Mail size={15} className="text-[#c2a15a] shrink-0" />
                {company.email}
              </a>
              <a
                href={`tel:${company.phone.replace(/[^+\d]/g, "")}`}
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <Phone size={15} className="text-[#c2a15a] shrink-0" />
                {company.phone}
              </a>
              <a
                href={`tel:${company.phoneDE.replace(/[^+\d]/g, "")}`}
                data-testid="footer-phone-de"
                className="flex items-start gap-2 hover:text-white transition-colors"
              >
                <Phone size={15} className="text-[#c2a15a] shrink-0 mt-0.5" />
                <span>
                  {company.phoneDE}
                  <span className="block text-[12px] text-white/45">{company.phoneDENote}</span>
                </span>
              </a>
            </div>

            <a
              href="https://www.linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-6 w-10 h-10 rounded-full bg-white/5 hover:bg-[#c2a15a] hover:text-[#0b1f3a] justify-center transition-colors"
              aria-label={tr("LinkedIn")}
            >
              <Linkedin size={18} />
            </a>
          </div>

          {/* Link columns */}
          {linkCols.map((col) => (
            <div key={col.title}>
              <h4 className="text-[13px] uppercase tracking-widest text-[#c2a15a] mb-4">
                {col.title}
              </h4>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <button
                      onClick={() => go(l.to)}
                      className="text-[14px] text-white/60 hover:text-white transition-colors text-left"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Regulatory */}
        <div className="border-t border-white/10 pt-8 text-[12px] text-white/40 leading-relaxed space-y-3">
          <p>
            {company.legalName} {tr("is a")} {company.legalForm} {tr("registered in England and Wales (Registration No.")} {company.registration}{tr("), registered office")} {company.address}{tr(". Authorised and regulated by the")}{" "}
            {company.regulator} {tr("(Firm No.")} {company.sraFirm}{tr("). Incorporated")}{" "}
            {company.incorporated}.
          </p>
          <p>
            {tr("FSCS protection applies to eligible deposits held with UK-authorised banks and building societies, subject to per-institution limits. Figures shown on this website are illustrative only and do not constitute financial, tax or legal advice.")}
          </p>
          <div className="flex flex-col sm:flex-row justify-between gap-2 pt-4">
            <span>
              © {new Date().getFullYear()} {company.legalName}{tr(". All rights reserved.")}
            </span>
            <span className="flex gap-5">
              <button
                onClick={() => go("/privacy")}
                className="hover:text-white transition-colors"
              >
                {tr("Privacy Policy")}
              </button>
              <button
                onClick={() => go("/terms")}
                className="hover:text-white transition-colors"
              >
                {tr("Terms")}
              </button>
              <button
                onClick={() => go("/cookies")}
                className="hover:text-white transition-colors"
              >
                {tr("Cookies")}
              </button>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
